import * as React from "react";
import { ChevronDown, Sparkles, Store } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  useChat,
} from "randmarcomps";
import {
  useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQuery,
  useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdSalesStatisticsQuery,
  useGetV4PartnerByApplicationIdAccountManufacturerQuery,
} from "randmar-api-client";
import SalesHistoryChart from "../components/charts/SalesHistoryChart";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import OpportunitiesGrid from "../components/grids/OpportunitiesGrid";
import ReturnGrid from "../components/grids/ReturnGrid";
import ResellerActions from "../components/reseller/ResellerActions";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import ResellerProfileCard from "../componentsCore/cards/ResellerProfileCard";
import SalesSummary from "../componentsCore/cards/SalesSummary";
import type { IResellerInfo, ISalesStatistics } from "../api/reseller/ResellersApi";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import Reseller, { SalesStatistics } from "../stores/reseller/Reseller";
import withRoot from "../withRoot";

const sectionIds = {
  activeOrders: "reseller-active-orders",
  completedOrders: "reseller-completed-orders",
  returns: "reseller-returns",
  opportunities: "reseller-opportunities",
  qualifications: "reseller-qualifications",
};

const formatDate = (value?: number) => {
  if (!value) return "No activity yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity yet";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

function buildResellerAssistantPrompt(options: {
  applicationId: string;
  manufacturerName: string;
  resellerId: string;
  resellerName: string;
  isQualified: boolean;
  onHold: boolean;
  qualificationId?: string;
  lastInvoiceDateLabel: string;
  joinedDateLabel: string;
  activityCounts: {
    activeOrders: number;
    completedOrders: number;
    returns: number;
    opportunities: number;
  };
  salesSummary: {
    salesLast30Days: number;
    salesLast3Months: number;
    salesYearToDay: number;
  };
}) {
  const lines: string[] = [
    "You are assisting with the Randmar Manufacturer Portal Reseller page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Manufacturer name: ${options.manufacturerName}`,
    "",
    `Reseller: ${options.resellerName} (${options.resellerId})`,
    `Status: ${options.onHold ? "On hold" : "Active"}`,
    `Qualified for this manufacturer: ${options.isQualified ? "Yes" : "No"}`,
    options.qualificationId ? `Qualification ID: ${options.qualificationId}` : "Qualification ID: None",
    "",
    `Last invoice: ${options.lastInvoiceDateLabel}`,
    `Joined Randmar: ${options.joinedDateLabel}`,
    "",
    "Activity counts:",
    `- Active orders: ${options.activityCounts.activeOrders}`,
    `- Completed orders: ${options.activityCounts.completedOrders}`,
    `- Returns: ${options.activityCounts.returns}`,
    `- Opportunities: ${options.activityCounts.opportunities}`,
    "",
    "Sales summary:",
    `- Last 30 days: ${options.salesSummary.salesLast30Days}`,
    `- Last 3 months: ${options.salesSummary.salesLast3Months}`,
    `- Year to day: ${options.salesSummary.salesYearToDay}`,
    "",
    "Please answer questions and suggest next actions based only on the page context above.",
  ];

  return lines.join("\n");
}

const ResellerPage: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { resellerId } = useParams<{ resellerId: string }>();
  const { sendMessageWithClear } = useChat();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
  const [highlightedQualification, setHighlightedQualification] = React.useState<string | null>(null);
  const quickStatsHeadingId = React.useId();
  const qualificationHeadingId = React.useId();

  const { startDateIso, endDateIso } = React.useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setFullYear(end.getFullYear() - 5);
    return { startDateIso: start.toISOString(), endDateIso: end.toISOString() };
  }, []);

  const {
    data: manufacturer,
    refetch: refetchManufacturer,
  } = useGetV4PartnerByApplicationIdAccountManufacturerQuery(
    { applicationId, withSpecification: false },
    { skip: !applicationId }
  );

  const {
    data: resellerApi,
    isLoading: isResellerLoading,
    isFetching: isResellerFetching,
    isError: isResellerError,
    refetch: refetchReseller,
  } = useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQuery(
    { routeManufacturerId: applicationId, resellerId: resellerId ?? "", withSpecification: true },
    { skip: !applicationId || !resellerId }
  );

  const {
    data: resellerSalesApi,
    isFetching: isSalesFetching,
    isError: isSalesError,
    refetch: refetchSales,
  } = useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdSalesStatisticsQuery(
    { routeManufacturerId: applicationId, resellerId: resellerId ?? "", startDate: startDateIso, endDate: endDateIso },
    { skip: !applicationId || !resellerId }
  );

  const reseller = React.useMemo(() => {
    if (!resellerApi) return null;
    const converted = Reseller.convertFrom(resellerApi as unknown as IResellerInfo);
    const qualificationForManufacturer = converted.qualifications?.find((q) => q.manufacturerId === applicationId)?.qualificationId;
    converted.isQualified = Boolean(
      qualificationForManufacturer && qualificationForManufacturer !== "Do Not Sell" && qualificationForManufacturer !== "Suspended"
    );
    return converted;
  }, [applicationId, resellerApi]);

  const resellerSalesData = React.useMemo(() => {
    if (!resellerSalesApi) return [];
    return (resellerSalesApi as unknown as ISalesStatistics[]).map(SalesStatistics.convertFrom);
  }, [resellerSalesApi]);

  const isFetching = isResellerFetching || isSalesFetching;

  React.useEffect(() => {
    if ((resellerApi || resellerSalesApi) && !isFetching) {
      setLastRefreshedAt(new Date());
    }
  }, [isFetching, resellerApi, resellerSalesApi]);

  const ownQualification = React.useMemo(() => {
    if (!reseller) return undefined;
    return reseller.qualifications?.find(
      (q) => q.manufacturerId === applicationId && q.qualificationId !== "Do Not Sell" && q.qualificationId !== "Suspended"
    );
  }, [applicationId, reseller]);

  const otherQualifications = React.useMemo(() => {
    if (!reseller?.qualifications) return [];
    return reseller.qualifications.filter((q) => q.manufacturerId !== applicationId);
  }, [applicationId, reseller?.qualifications]);

  const highlightedQualificationCopy = React.useMemo(() => {
    if (!highlightedQualification) return null;
    const match = reseller?.qualifications?.find((q) => q.manufacturerId === highlightedQualification);
    if (!match) return null;
    return `${match.manufacturerPublicName || "Partner"} • ${match.qualificationId || "Qualification"}`;
  }, [highlightedQualification, reseller?.qualifications]);

  const quickStats = React.useMemo(() => {
    if (!reseller) return [];
    const activeCount = reseller.activeOrderDetails?.length ?? 0;
    const completeCount = reseller.completedOrderDetails?.length ?? 0;
    const returnsCount = reseller.returns?.length ?? 0;
    const opportunitiesCount = reseller.opportunities?.length ?? 0;
    const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

    return [
      {
        id: sectionIds.activeOrders,
        label: "Active orders",
        value: numberFormatter.format(activeCount),
        helper: activeCount ? "Tap to review open POs" : "No open orders",
        href: activeCount ? `#${sectionIds.activeOrders}` : undefined,
      },
      {
        id: sectionIds.completedOrders,
        label: "Completed orders",
        value: numberFormatter.format(completeCount),
        helper: completeCount ? "Shipped & invoiced" : "Nothing completed yet",
        href: completeCount ? `#${sectionIds.completedOrders}` : undefined,
      },
      {
        id: sectionIds.returns,
        label: "Returns",
        value: numberFormatter.format(returnsCount),
        helper: returnsCount ? "Defectives logged" : "No returns on record",
        href: returnsCount ? `#${sectionIds.returns}` : undefined,
      },
      {
        id: sectionIds.opportunities,
        label: "Opportunities",
        value: numberFormatter.format(opportunitiesCount),
        helper: opportunitiesCount ? "Bids & rebates" : "No bids yet",
        href: opportunitiesCount ? `#${sectionIds.opportunities}` : undefined,
      },
    ];
  }, [reseller]);

  React.useEffect(() => {
    const previousTitle = document.title;
    if (reseller) {
      const resellerLabel = reseller.publicName || reseller.name || resellerId || "Reseller";
      document.title = `Reseller — ${resellerLabel} | ${previousTitle}`;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [reseller, resellerId]);

  const pageContainerClassName = "mx-auto w-full max-w-[1440px] space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([refetchReseller(), refetchSales(), refetchManufacturer()]);
  }, [refetchManufacturer, refetchReseller, refetchSales]);

  const assistantPrompt = React.useMemo(() => {
    if (!reseller || !resellerId) return null;
    const qualificationId = reseller.qualifications?.find((row) => row.manufacturerId === applicationId)?.qualificationId;
    const manufacturerName = manufacturer?.PublicName || manufacturer?.Name || "Manufacturer";
    const resellerName = reseller.publicName || reseller.name || "Reseller";

    return buildResellerAssistantPrompt({
      applicationId,
      manufacturerName,
      resellerId,
      resellerName,
      isQualified: Boolean(reseller.isQualified),
      onHold: Boolean(reseller.onHold),
      qualificationId,
      lastInvoiceDateLabel: formatDate(reseller.lastInvoiceDate),
      joinedDateLabel: formatDate(reseller.creationDate),
      activityCounts: {
        activeOrders: reseller.activeOrderDetails?.length ?? 0,
        completedOrders: reseller.completedOrderDetails?.length ?? 0,
        returns: reseller.returns?.length ?? 0,
        opportunities: reseller.opportunities?.length ?? 0,
      },
      salesSummary: {
        salesLast30Days: reseller.salesData?.salesLast30Days ?? 0,
        salesLast3Months: reseller.salesData?.salesLast3Months ?? 0,
        salesYearToDay: reseller.salesData?.salesYearToDay ?? 0,
      },
    });
  }, [applicationId, manufacturer?.Name, manufacturer?.PublicName, reseller, resellerId]);

  if (!resellerId) {
    return (
      <div className={pageContainerClassName}>
        <div className={surfaceClassName}>
          <Alert variant="destructive" className="rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Missing reseller</AlertTitle>
            <AlertDescription>
              This URL is missing a reseller ID. Return to{" "}
              <Link to={Path.resellers(applicationId)} className="font-semibold text-[color:var(--ops-accent)] hover:underline">
                Resellers
              </Link>
              .
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (isResellerLoading && !reseller) {
    return (
      <div className={pageContainerClassName} role="status" aria-live="polite">
        <div className={surfaceClassName}>
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="mt-3 h-5 w-1/3" />
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full rounded-2xl" />
            ))}
          </div>
        </div>
        <Skeleton className="h-[520px] w-full rounded-2xl" />
      </div>
    );
  }

  if (!reseller) {
    return (
      <div className={pageContainerClassName}>
        <div className={surfaceClassName}>
          <Alert variant="destructive" className="rounded-2xl border border-red-100 bg-white">
            <AlertTitle>Couldn’t load reseller</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>We ran into an issue loading this reseller profile. Please try again.</p>
              <Button type="button" variant="outline" onClick={() => refetchReseller()} className="rounded-xl">
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const actionsMenu = (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="inline-flex items-center gap-2 rounded-xl">
          Actions
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto rounded-xl border border-gray-100 bg-white p-0 shadow-sm">
        <ResellerActions reseller={reseller} />
      </PopoverContent>
    </Popover>
  );

  const headerActions = (
    <div className="flex flex-col gap-3 sm:items-end">
      <DataRefreshBar
        align="right"
        lastRefreshedAt={lastRefreshedAt}
        isRefreshing={isFetching}
        onRefresh={handleRefresh}
        actions={(
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => assistantPrompt && sendMessageWithClear(assistantPrompt)}
            className="rounded-xl"
            aria-label="Open assistant with this page context"
            disabled={!assistantPrompt}
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      />
      {actionsMenu}
    </div>
  );

  const footer = isResellerError || isSalesError ? (
    <Alert variant="destructive" className="rounded-2xl border border-red-100 bg-white">
      <AlertTitle>Some data couldn’t be refreshed</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>Parts of this page may be out of date. Try refreshing again.</p>
      </AlertDescription>
    </Alert>
  ) : null;

  const manufacturerName = manufacturer?.PublicName || manufacturer?.Name || "Manufacturer";
  const hasAnyActivity =
    (reseller.activeOrderDetails?.length ?? 0) +
      (reseller.completedOrderDetails?.length ?? 0) +
      (reseller.returns?.length ?? 0) +
      (reseller.opportunities?.length ?? 0) >
    0;

  return (
    <div className={pageContainerClassName} aria-busy={isFetching}>
      <ResellerProfileCard reseller={reseller} actions={headerActions} footer={footer} />

      <section className="grid gap-6 lg:grid-cols-3" aria-label="Sales overview">
        <div className={["lg:col-span-2", surfaceClassName].join(" ")}>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Performance</p>
          <h2 className="mt-2 text-[24px] font-semibold text-slate-900">Sales summary</h2>
          <p className="mt-1 text-[15px] text-slate-500">A quick snapshot of trailing sales for this reseller.</p>
          <div className="mt-4">
            <SalesSummary
              salesData={reseller.salesData}
              isQualified={reseller.isQualified}
              fiscalYearMonthStart={manufacturer?.FiscalYearMonthStart}
            />
          </div>
        </div>

        <aside className={surfaceClassName} aria-label="Relationship details">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Relationship</p>
          <h2 className="mt-2 text-[20px] font-medium text-slate-900">Key dates</h2>
          <dl className="mt-4 space-y-4">
            <div className="rounded-2xl bg-slate-50 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last invoice</dt>
              <dd className="mt-2 text-[15px] font-semibold text-slate-900">{formatDate(reseller.lastInvoiceDate)}</dd>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Joined Randmar</dt>
              <dd className="mt-2 text-[15px] font-semibold text-slate-900">{formatDate(reseller.creationDate)}</dd>
            </div>
          </dl>
        </aside>
      </section>

      <section className={surfaceClassName} aria-labelledby={quickStatsHeadingId}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow overview</p>
            <h2 id={quickStatsHeadingId} className="mt-2 text-[24px] font-semibold text-slate-900">
              Pipeline health
            </h2>
          </div>
          <p className="text-[15px] text-slate-500">Jump into the sections that need attention.</p>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {quickStats.map((stat) =>
            stat.href ? (
              <a
                key={stat.id}
                href={stat.href}
                className="group rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--ops-accent)]"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{stat.value}</p>
                <p className="mt-1 text-[13px] text-slate-500">{stat.helper}</p>
              </a>
            ) : (
              <div key={stat.id} className="rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-5" aria-disabled="true">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{stat.value}</p>
                <p className="mt-1 text-[13px] text-slate-500">{stat.helper}</p>
              </div>
            )
          )}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-12" aria-labelledby={qualificationHeadingId}>
        <div className={["lg:col-span-7", surfaceClassName].join(" ")} id={sectionIds.qualifications}>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partner fit</p>
          <h2 id={qualificationHeadingId} className="mt-2 text-[24px] font-semibold text-slate-900">
            Qualifications
          </h2>
          <p className="mt-1 text-[15px] text-slate-500">Qualification footprint across Randmar partners.</p>

          {(reseller.qualifications?.length ?? 0) > 0 ? (
            <div className="mt-6 space-y-6">
              {ownQualification && (
                <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                  <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                    <img
                      className="h-16 w-16 rounded-2xl border border-gray-100 object-contain p-2"
                      src={`https://api.randmar.io/V4/Partner/${applicationId}/Account/Logo?width=100`}
                      alt={`${manufacturerName} logo`}
                      loading="lazy"
                    />
                    <div>
                      <p className="text-[13px] text-slate-500">Your brand</p>
                      <p className="text-[20px] font-medium text-slate-900">{manufacturerName}</p>
                      <p className="mt-1 text-[15px] text-slate-500">
                        Qualification ID: <span className="font-semibold text-slate-900">{ownQualification.qualificationId}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {otherQualifications.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Other partners</h3>
                  <ul className="mt-3 flex flex-wrap gap-3" role="list">
                    {otherQualifications.map((row) => (
                      <li key={row.manufacturerId || row.manufacturerPublicName || "partner"}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlightedQualification(row.manufacturerId || null)}
                          onFocus={() => setHighlightedQualification(row.manufacturerId || null)}
                          onMouseLeave={() => setHighlightedQualification(null)}
                          onBlur={() => setHighlightedQualification(null)}
                          className="group flex h-16 w-16 items-center justify-center rounded-2xl border border-gray-100 bg-white p-2 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                          aria-pressed={highlightedQualification === row.manufacturerId}
                        >
                          <img
                            src={`https://api.randmar.io/V4/Partner/${row.manufacturerId}/Account/Logo?width=100`}
                            alt={`${row.manufacturerPublicName || "Manufacturer"} logo`}
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {highlightedQualificationCopy && (
                    <p className="mt-2 text-[13px] text-slate-500" role="status">
                      {highlightedQualificationCopy}
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-5">
              <p className="text-[15px] text-slate-500">No qualifications have been recorded for this reseller yet.</p>
            </div>
          )}
        </div>

        <aside className={["lg:col-span-5", surfaceClassName].join(" ")} aria-label="Sales history">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Velocity</p>
          <h2 className="mt-2 text-[20px] font-medium text-slate-900">Sales history</h2>
          <p className="mt-1 text-[15px] text-slate-500">Trailing 12 months of units sold.</p>
          <div className="mt-4">
            <SalesHistoryChart sales={resellerSalesData} />
          </div>
        </aside>
      </section>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2" aria-label="Orders and returns">
        {(reseller.activeOrderDetails || []).length > 0 && (
          <div className={surfaceClassName} id={sectionIds.activeOrders}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Live fulfillment</p>
            <h2 className="mt-2 text-[20px] font-medium text-slate-900">Active orders</h2>
            <p className="mt-1 text-[15px] text-slate-500">Open purchase orders currently in motion.</p>
            <div className="mt-6 space-y-4">
              <OpenOrdersSummary openOrders={reseller.activeOrderDetails} />
              <OpenOrdersGrid productOrders={reseller.activeOrderDetails} withHeader />
            </div>
          </div>
        )}

        {(reseller.completedOrderDetails || []).length > 0 && (
          <div className={surfaceClassName} id={sectionIds.completedOrders}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fulfilled</p>
            <h2 className="mt-2 text-[20px] font-medium text-slate-900">Completed orders</h2>
            <p className="mt-1 text-[15px] text-slate-500">Shipped and invoiced.</p>
            <div className="mt-6">
              <OpenOrdersGrid productOrders={reseller.completedOrderDetails} defaultRowsPerPage={10} withHeader />
            </div>
          </div>
        )}

        {reseller.returns && reseller.returns.length > 0 && (
          <div className={surfaceClassName} id={sectionIds.returns}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Post-sale</p>
            <h2 className="mt-2 text-[20px] font-medium text-slate-900">Returns</h2>
            <p className="mt-1 text-[15px] text-slate-500">Defectives and claims.</p>
            <div className="mt-6">
              <ReturnGrid returns={reseller.returns} />
            </div>
          </div>
        )}

        {reseller.opportunities && reseller.opportunities.length > 0 && (
          <div className={surfaceClassName} id={sectionIds.opportunities}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bids</p>
            <h2 className="mt-2 text-[20px] font-medium text-slate-900">Opportunities</h2>
            <p className="mt-1 text-[15px] text-slate-500">Bids and rebates.</p>
            <div className="mt-6">
              <OpportunitiesGrid opportunities={reseller.opportunities} />
            </div>
          </div>
        )}

        {!hasAnyActivity && (
          <div className={surfaceClassName}>
            <div className="mx-auto flex max-w-lg flex-col items-center gap-3 py-10 text-center">
              <Store className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <p className="text-xl font-medium text-slate-900">No activity yet</p>
              <p className="text-[15px] text-slate-500">
                Orders, returns, and opportunities will appear here as this reseller starts transacting.
              </p>
              <div className="mt-2">
                <Button asChild size="sm" className="rounded-xl">
                  <Link to={Path.resellers(applicationId)}>Back to resellers</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default withRoot(ResellerPage);
